<template>
    <div v-if="loading" class="card">
        <!-- Skeleton loader remains the same -->
        <div class="p-fluid grid">
            <div class="col-12">
                <Skeleton height="2rem" width="30%" class="mb-4"></Skeleton>
                <Skeleton height="15rem"></Skeleton>
            </div>
        </div>
    </div>
    <form v-else name="form-member" id="form-member" autocomplete="off" novalidate @submit="onSubmit">
        <input type="hidden" name="id" v-model="model.id">

        <div class="card p-3 md:p-4">
            <TabView class="tabview-custom" v-model:activeIndex="activeTabIndex" :scrollable="true">
                <TabPanel>
                    <template #header>
                        <i class="pi pi-user mr-2"></i>
                        <span>Personal Information</span>
                        <i v-if="tabHasError[0]"
                           class="pi pi-exclamation-circle tab-error-mark ml-2"
                           :aria-label="tabErrorLabel(0)"></i>
                    </template>
                    <div class="p-fluid formgrid grid mt-4">
                        <div class="field col-12 md:col-6">
                            <label for="name">Member Name <span class="required-mark" aria-hidden="true">*</span></label>
                            <InputText id="name" v-model="model.name" aria-required="true"
                                       :aria-invalid="fieldErrors.name ? 'true' : 'false'"
                                       :aria-describedby="fieldErrors.name ? 'name-error' : undefined"
                                       :class="{ 'p-invalid': fieldErrors.name }" />
                            <small v-if="fieldErrors.name" id="name-error" class="p-error">{{ fieldErrors.name }}</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="username">Login ID</label>
                            <InputText id="username" v-model="model.username" aria-describedby="username-help" />
                            <small id="username-help" class="field-help">The ID this member signs in to the mobile app with. Leave blank if they do not need access.</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="preferredBadgeName">Preferred Badge Name <span class="required-mark" aria-hidden="true">*</span></label>
                            <InputText id="preferredBadgeName" v-model="model.preferredBadgeName" aria-required="true"
                                       :aria-invalid="fieldErrors.preferredBadgeName ? 'true' : 'false'"
                                       :aria-describedby="fieldErrors.preferredBadgeName ? 'preferredBadgeName-error' : 'preferredBadgeName-help'"
                                       :class="{ 'p-invalid': fieldErrors.preferredBadgeName }" />
                            <small v-if="fieldErrors.preferredBadgeName" id="preferredBadgeName-error" class="p-error">{{ fieldErrors.preferredBadgeName }}</small>
                            <small v-else id="preferredBadgeName-help" class="field-help">The name printed on the conference badge.</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <span id="gender-label" class="field-label">Gender <span class="required-mark" aria-hidden="true">*</span></span>
                            <div class="gender-select">
                                <SelectButton
                                    v-model="model.gender"
                                    :options="genderOptions"
                                    optionLabel="name"
                                    optionValue="value"
                                    :allowEmpty="false"
                                    aria-labelledby="gender-label"
                                />
                            </div>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="otherSpices">Other Spices</label>
                            <InputText id="otherSpices" v-model="model.otherSpices" aria-describedby="otherSpices-help" />
                            <small id="otherSpices-help" class="field-help">Spices this member trades beyond their main commodity.</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="foodTypeName">Food Type</label>
                            <InputText id="foodTypeName" v-model="model.foodTypeName" aria-describedby="foodTypeName-help" />
                            <small id="foodTypeName-help" class="field-help">Catering preference, e.g. Halal or Vegetarian.</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="shirtSize">Shirt Size</label>
                            <InputText id="shirtSize" v-model="model.shirtSize" />
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="paymentMethodName">Payment Method</label>
                            <InputText id="paymentMethodName" v-model="model.paymentMethodName" />
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="registeredEventId">Event Registration</label>
                            <Dropdown
                                inputId="registeredEventId"
                                v-model="model.registeredEventId"
                                :options="events"
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Not registered to an event"
                                :showClear="true"
                                :loading="eventsLoading"
                            />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel>
                    <template #header>
                        <i class="pi pi-building mr-2"></i>
                        <span>Company Information</span>
                        <i v-if="tabHasError[1]"
                           class="pi pi-exclamation-circle tab-error-mark ml-2"
                           :aria-label="tabErrorLabel(1)"></i>
                    </template>
                    <div class="p-fluid formgrid grid mt-4">
                        <div class="field col-12 md:col-6">
                            <label for="organizationName">Company Name <span class="required-mark" aria-hidden="true">*</span></label>
                            <InputText id="organizationName" v-model="model.organizationName" aria-required="true"
                                       :aria-invalid="fieldErrors.organizationName ? 'true' : 'false'"
                                       :aria-describedby="fieldErrors.organizationName ? 'organizationName-error' : undefined"
                                       :class="{ 'p-invalid': fieldErrors.organizationName }" />
                            <small v-if="fieldErrors.organizationName" id="organizationName-error" class="p-error">{{ fieldErrors.organizationName }}</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="designation">Designation <span class="required-mark" aria-hidden="true">*</span></label>
                            <InputText id="designation" v-model="model.designation" aria-required="true"
                                       :aria-invalid="fieldErrors.designation ? 'true' : 'false'"
                                       :aria-describedby="fieldErrors.designation ? 'designation-error' : 'designation-help'"
                                       :class="{ 'p-invalid': fieldErrors.designation }" />
                            <small v-if="fieldErrors.designation" id="designation-error" class="p-error">{{ fieldErrors.designation }}</small>
                            <small v-else id="designation-help" class="field-help">Job title as it should appear on the badge.</small>
                        </div>
                        <div class="field col-12">
                            <label for="organizationDescription">Company Description</label>
                            <Textarea id="organizationDescription" v-model="model.organizationDescription" rows="3" autoResize />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel>
                    <template #header>
                        <i class="pi pi-address-book mr-2"></i>
                        <span>Contact &amp; Address</span>
                        <i v-if="tabHasError[2]"
                           class="pi pi-exclamation-circle tab-error-mark ml-2"
                           :aria-label="tabErrorLabel(2)"></i>
                    </template>
                    <div class="p-fluid formgrid grid mt-4 contact-address-form">
                        <!-- Row 1 -->
                        <div class="field col-12 md:col-6">
                            <label for="email">Email <span class="required-mark" aria-hidden="true">*</span></label>
                            <InputText id="email" type="email" v-model="model.email" aria-required="true"
                                       :aria-invalid="fieldErrors.email ? 'true' : 'false'"
                                       :aria-describedby="fieldErrors.email ? 'email-error' : undefined"
                                       :class="{ 'p-invalid': fieldErrors.email }" />
                            <small v-if="fieldErrors.email" id="email-error" class="p-error">{{ fieldErrors.email }}</small>
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="phoneNumber">Phone Number</label>
                            <InputText id="phoneNumber" type="tel" inputmode="tel" v-model="model.phoneNumber" />
                        </div>
                        <!-- Row 2 -->
                        <div class="field col-12 md:col-6">
                            <label for="website">Website</label>
                            <InputText id="website" type="url" inputmode="url" v-model="model.website" />
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="fax">Fax</label>
                            <InputText id="fax" type="tel" inputmode="tel" v-model="model.fax" />
                        </div>
                        <!-- Row 3 -->
                        <div class="field col-12 md:col-6">
                            <label for="cityName">City Name</label>
                            <InputText id="cityName" v-model="model.cityName" />
                        </div>
                        <div class="field col-12 md:col-6">
                            <label for="countryName">Country Name</label>
                            <InputText id="countryName" v-model="model.countryName" />
                        </div>
                        <!-- Row 4 (Full Width) -->
                        <div class="field col-12">
                            <label for="address">Address <span class="required-mark" aria-hidden="true">*</span></label>
                            <Textarea id="address" v-model="model.address" rows="3" autoResize aria-required="true"
                                      :aria-invalid="fieldErrors.address ? 'true' : 'false'"
                                      :aria-describedby="fieldErrors.address ? 'address-error' : undefined"
                                      :class="{ 'p-invalid': fieldErrors.address }" />
                            <small v-if="fieldErrors.address" id="address-error" class="p-error">{{ fieldErrors.address }}</small>
                        </div>
                    </div>
                </TabPanel>
            </TabView>

            <div class="flex justify-content-end gap-2 mt-4 mb-3 md:mb-1">
                <Button label="Cancel" severity="secondary" outlined @click="onCancel" />
                <Button type="submit" :label="isAddMode ? 'Add Member' : 'Submit'" :loading="saving" />
            </div>
        </div>
    </form>
</template>

<script>
import { defineComponent } from 'vue';
import apiService from "@/services/ApiService";

// PrimeVue Components
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import SelectButton from "primevue/selectbutton";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Skeleton from "primevue/skeleton";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

const TAB_NAMES = ['Personal Information', 'Company Information', 'Contact & Address'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default defineComponent({
    name: "MemberForm",
    props: {
        id: {
            type: String,
            required: false
        }
    },
    components: {
        InputText,
        Textarea,
        SelectButton,
        Dropdown,
        Button,
        Skeleton,
        TabView,
        TabPanel
    },
    computed: {
        isAddMode() {
            return !this.id;
        },
        // One flag per tab, so a tab header can keep showing that something
        // inside it needs attention after the validation toast is gone.
        tabHasError() {
            const flags = TAB_NAMES.map(() => false);
            this.requiredFields().forEach(field => {
                if (this.fieldErrors[field.key]) {
                    flags[field.tab] = true;
                }
            });
            return flags;
        }
    },
    data() {
        return {
            model: {
                id: '',
                name: '',
                gender: '1',
                preferredBadgeName: '',
                designation: '',
                organizationName: '',
                organizationDescription: '',
                address: '',
                cityName: '',
                countryName: '',
                email: '',
                phoneNumber: '',
                fax: '',
                website: '',
                username: '',
                otherSpices: '',
                foodTypeName: '',
                shirtSize: '',
                paymentMethodName: '',
                registeredEventId: ''
            },
            events: [],
            eventsLoading: false,
            genderOptions: [
                {name: 'Male', value: '1'},
                {name: 'Female', value: '2'}
            ],
            saving: false,
            loading: false,
            activeTabIndex: 0,
            // key -> message shown under the field. Empty object means valid.
            fieldErrors: {}
        }
    },
    watch: {
        // Clear a field's error as soon as it is filled in, so the red state
        // never outlives the problem. Only ever removes errors — they are added
        // on submit.
        model: {
            deep: true,
            handler() {
                if (Object.keys(this.fieldErrors).length === 0) {
                    return;
                }
                const remaining = {};
                this.requiredFields().forEach(field => {
                    if (!this.fieldErrors[field.key]) {
                        return;
                    }
                    const message = this.validateField(field);
                    if (message) {
                        remaining[field.key] = message;
                    }
                });
                this.fieldErrors = remaining;
            }
        }
    },
    methods: {
        requiredFields() {
            return [
                {key: 'name', label: 'Member Name', tab: 0, message: 'Enter the member\'s full name.'},
                {key: 'preferredBadgeName', label: 'Preferred Badge Name', tab: 0, message: 'Enter the name to print on the badge.'},
                {key: 'gender', label: 'Gender', tab: 0, message: 'Choose a gender.'},
                {key: 'organizationName', label: 'Company Name', tab: 1, message: 'Enter the company this member represents.'},
                {key: 'designation', label: 'Designation', tab: 1, message: 'Enter the member\'s job title.'},
                {key: 'email', label: 'Email', tab: 2, message: 'Enter an email address.'},
                {key: 'address', label: 'Address', tab: 2, message: 'Enter a postal address.'}
            ];
        },
        isFieldEmpty(field) {
            if (field.key === 'gender') {
                return !parseInt(this.model.gender, 10);
            }
            return !String(this.model[field.key] || '').trim();
        },
        // Returns the message to show, or '' when the field is fine.
        validateField(field) {
            if (this.isFieldEmpty(field)) {
                return field.message;
            }
            if (field.key === 'email' && !EMAIL_PATTERN.test(String(this.model.email).trim())) {
                return 'Enter a valid email address, e.g. name@company.com.';
            }
            return '';
        },
        tabErrorLabel(tabIndex) {
            return `${TAB_NAMES[tabIndex]} has fields that need attention`;
        },
        // "A", "A and B", "A, B and C"
        joinNames(names) {
            if (names.length < 2) {
                return names.join('');
            }
            return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
        },
        validateForm() {
            const fieldErrors = {};
            const invalidTabs = [];
            let firstInvalidTab = null;

            this.requiredFields().forEach(field => {
                const message = this.validateField(field);
                if (!message) {
                    return;
                }
                fieldErrors[field.key] = message;
                if (invalidTabs.indexOf(field.tab) === -1) {
                    invalidTabs.push(field.tab);
                }
                if (firstInvalidTab === null) {
                    firstInvalidTab = field.tab;
                }
            });

            this.fieldErrors = fieldErrors;
            if (Object.keys(fieldErrors).length === 0) {
                return true;
            }

            this.activeTabIndex = firstInvalidTab;
            this.focusFirstInvalidField();

            // Point at the tabs rather than listing field names: each field now
            // carries its own message and every affected tab header is marked.
            this.$toast.add({
                severity: 'error',
                summary: 'Some fields still need attention',
                detail: `Check ${this.joinNames(invalidTabs.map(i => TAB_NAMES[i]))}.`,
                life: 5000
            });
            return false;
        },
        focusFirstInvalidField() {
            const first = this.requiredFields().find(field => this.fieldErrors[field.key]);
            if (!first || first.key === 'gender') {
                return;
            }
            this.$nextTick(() => {
                const el = document.getElementById(first.key);
                if (el) {
                    el.focus();
                }
            });
        },
        buildPayload() {
            return {
                ...this.model,
                gender: parseInt(this.model.gender, 10),
                // Dropdown clears to null; the API has always been sent ''.
                registeredEventId: this.model.registeredEventId || ''
            };
        },
        onSubmit(event) {
            event.preventDefault();
            if (!this.validateForm()) {
                return;
            }
            this.saving = true;

            const endpoint = this.isAddMode ? '/member/add' : '/member/edit';
            const payload = this.buildPayload();

            apiService.post(endpoint, payload)
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: this.isAddMode ? 'Member has been added' : 'Member has been updated',
                        life: 5000
                    });
                    if (this.isAddMode) {
                        this.$router.push('/member');
                    }
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: this.isAddMode ? 'Failed to add member' : 'Failed to update member',
                        detail: error.response?.data?.Message || 'An error occurred',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            this.$router.push('/member');
        },
        getMemberById(id) {
            this.loading = true;
            apiService.get(`/member/${id}`)
                .then(response => {
                    this.model = response.data;
                    this.model.organizationDescription = this.model.companyDescription;
                    this.model.registeredEventId = this.model.registeredEventId || '';

                    const rawGender = this.model.gender ?? this.model.Gender ?? this.model.genderType ?? this.model.sex;
                    if (rawGender !== undefined && rawGender !== null) {
                        const g = String(rawGender).toLowerCase();
                        if (g === '1' || g === 'male' || g === 'm') this.model.gender = '1';
                        else if (g === '2' || g === 'female' || g === 'f') this.model.gender = '2';
                        else this.model.gender = '1'; // fallback
                    } else {
                        this.model.gender = '1';
                    }
                }).catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to get member',
                        detail: error.response?.data?.Message || 'An error occurred',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadEvents() {
            this.eventsLoading = true;
            apiService.get('/event')
                .then(response => {
                    this.events = response.data?.data || [];
                    if (this.isAddMode) {
                        return apiService.get('/event/active');
                    }
                    return null;
                })
                .then(response => {
                    if (response && response.data && response.data.id) {
                        this.model.registeredEventId = response.data.id;
                    }
                })
                .catch(error => {
                    console.log(error);
                    // Without this the dropdown just sits there empty and the
                    // operator has no idea the list failed to load.
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Could not load events',
                        detail: 'Event Registration is unavailable. Reload the page to try again.',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.eventsLoading = false;
                });
        }
    },
    mounted() {
        this.loadEvents();
        if (this.id) {
            this.getMemberById(this.id);
        }
    }
})
</script>

<style scoped>
.field {
    margin-bottom: 1rem;
}

/* Matches <label> typography for a control that has no single labelable
   element to point a `for` at (the gender toggle). */
.field-label {
    display: block;
    margin-bottom: 0.5rem;
}

.field-help {
    display: block;
    margin-top: 0.25rem;
    color: #6c757d;
    font-size: 0.8125rem;
}

.p-error {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.8125rem;
}

.tab-error-mark {
    color: #dc3545;
    font-size: 0.875rem;
}

/* Keeps the card from collapsing when switching to a tab with fewer fields —
   the submit buttons would otherwise jump up the page. */
:deep(.p-tabview-panels) {
    min-height: 24rem;
}

@media screen and (max-width: 767px) {
    /* The card, the tab panel and the grid each add their own inset; stacked
       they ate about a quarter of a 390px screen. */
    :deep(.p-tabview-panels) {
        padding-left: 0;
        padding-right: 0;
        min-height: 0;
    }
}

.gender-select {
    display: flex;
    width: fit-content;
    max-width: 280px;
}

.gender-select :deep(.p-selectbutton) {
    display: flex;
    gap: 0.5rem;
    padding: 0.375rem;
    border-radius: 8px;
    background: var(--surface-50, #f8f9fa);
    border: 1px solid var(--surface-200, #dee2e6);
}

.gender-select :deep(.p-button) {
    flex: 1;
    min-width: 80px;
    height: 2.25rem;
    border-radius: 6px;
    box-shadow: none;
    border: none;
    font-weight: 500;
    font-size: 0.875rem;
    transition: all 0.15s ease-in-out;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 1rem;
}

.gender-select :deep(.p-button:not(.p-highlight)) {
    background: transparent;
    color: var(--text-color-secondary, #6c757d);
}

.gender-select :deep(.p-button:not(.p-highlight):hover) {
    background: var(--surface-100, #f1f3f4);
    color: var(--text-color, #495057);
}

.gender-select :deep(.p-button.p-highlight) {
    background: #4e73df;
    color: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.gender-select :deep(.p-button.p-highlight):hover {
    background: #2e59d9;
}

/* Must stay last: the rules above set `box-shadow` at a specificity that beats
   the theme's `.p-button:focus-visible`, which left the toggle with no visible
   keyboard focus at all. */
.gender-select :deep(.p-button:focus-visible) {
    outline: 0 none;
    box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.5);
    position: relative;
    z-index: 1;
}

/* Ensure textarea in address field doesn't grow too tall */
textarea {
    min-height: 4rem;
    max-height: 6rem;
}

/* Contact & Address inherits default formgrid look; keep only textarea tweak if needed */
.contact-address-form :deep(textarea) {
    resize: vertical;
}

.required-mark {
    color: #dc3545;
    font-weight: 700;
}
</style>
